import { defineStore } from "pinia";

import axios from "../../axios-auth";
import { pathOr } from "ramda";
import {
  i18n,
  PREFIX,
  addDefaultTranslations,
  setLanguageAttributes
} from "../../i18n.js";
import { v4 as uuidv4 } from "uuid";

import constants from "../../helpers/constants";
import qs from "qs";
import { mergeDenyRules } from "../../helpers/helpers";
import _ from "lodash";
import hash from "object-hash";

import { useChangeOptionsStore } from "@/stores/modules/changeOptions";
import { useCheckedPageOptionsStore } from "@/stores/modules/checkedPageOptions";
import { useMyAccountStore } from "@/stores/modules/myAccount";
import { usePersonalizeStore } from "@/stores/modules/personalize";
import { useUploadsStore } from "@/stores/modules/uploads";
import { useCustomStore } from "@/stores/modules/custom";
import { useConfigStore } from "@/stores/modules/config";
import { useBundleStore } from "@/stores/modules/bundle";
import { useUrlsStore } from "@/stores/modules/urls";
import { usePreview3DStore } from "@/stores/modules/preview3D";
import { useQuantityFieldStore } from "@/stores/modules/quantityField";
import { usePreflightPitstopStore } from "@/stores/modules/preflightPitstop";
import { usePreflightDataStore } from "@/stores/modules/preflightData";
import { usePackingOptionsStore } from "@/stores/modules/packingOptions";
import { useImageEditorStore } from "@/stores/modules/imageEditor";
import { useCustomerStore } from "@/stores/modules/customer";
import { useUpdatesStore } from "@/stores/modules/updates";
import { useCustomerNotesStore } from "@/stores/modules/customerNotes";
import { useQuoteStore } from "@/stores/modules/quote";

const productPartsOptionCode = "product_parts";

export const useWebproductStore = defineStore("webproduct", {
  state: () => ({
    data: {},
    webProductId: 0,
    webProductSku: "",
    selectionId: 0,
    activeProductPartCode: "",
    activePageOptions: {},
    isLoading: false,
    numberOfUploads: [],
    networkError: false,
    networkMessage: "",
    filesNeedToBeConnected: true,
    filesConnectLoading: false,
    numberOfActiveUploads: 0,
    selector: "",
    selectedTemplateId: null,
    theme: "",
    config: null,
    storeCode: null,
    isMobile: false,
    vueInstance: null,
    hideInitialLoading: true
  }),

  getters: {
    getShowCustomerNotes(state) {
      return Object.keys(this.getCustomerNotes).length;
    },
    getCustomerNotes: state => {
      return pathOr({}, ["data", "config", "customer_notes"], state);
    },
    getIsPackagingAttrUpdateEnabled: state => {
      return state.data.config.packaging_update_attributes
        ? parseInt(state.data.config.packaging_update_attributes)
        : false;
    },
    getHasInitialChangeOptionsResponse(state) {
      const customStore = useCustomStore();
      var qties = customStore.getCustomQuantitiesLabels;
      if (qties.length > 0) {
        // we need to run changeOptions to get prices also for these custom quantities
        return false;
      }

      if (this.getPersonalizationQuantityWithPrice) {
        // we need to run changeOptions to get prices also for this type of product
        return false;
      }

      return pathOr(false, ["data", "initialChangeOptionsResponse"], state);
    },
    getHideInitialLoading(state) {
      if (!this.getShowMatrixInitialLoading) {
        return state.hideInitialLoading;
      }
      return false;
    },
    getShowMatrixInitialLoading(state) {
      return pathOr(true, ["config", "showInitialLoading"], state);
    },
    getUploadPersonalizationOneProduct(state) {
      return state.data.config.upload_personalization_one_product
        ? parseInt(state.data.config.upload_personalization_one_product)
        : 0;
    },
    getIsFullPageType(state) {
      if (
        [
          "upload_fullpage_configuration",
          "personalization_fullpage_configuration",
          "upload_personalization_fullpage_configuration"
        ].includes(state.data.product_type)
      ) {
        return true;
      }
      return false;
    },
    getUploadPersonalizationOneProductPPType(state) {
      return function (type) {
        var items = state.data.productparts.filter(item => {
          if (
            item.attributes &&
            item.attributes.content_type &&
            item.attributes.content_type == type
          ) {
            return true;
          }
          return false;
        });
        if (items.length > 0) {
          return items[0];
        }
        // else return the first one
        return state.data.productparts[0];
      };
    },
    getTransitionUsedElement(state) {
      return this.getUseTransition ? "appTransitionLocal" : "appFragment";
    },
    getUseTransition(state) {
      return state.data.config && state.data.config.activate_transition
        ? parseInt(state.data.config.activate_transition)
        : 0;
    },
    getMaxUploadFileSize(state) {
      return state.data.config.upload_filesize
        ? parseInt(state.data.config.upload_filesize)
        : 100;
    },
    getAllowedUploadedFile(state) {
      let fileExtensions = this.getAllowedFilesExtensions;
      const mimeTypes = constants.MIME_TYPES;
      const acceptedMimes = [];
      fileExtensions.map(el => {
        const type = el.toUpperCase();
        if (mimeTypes.hasOwnProperty(type)) {
          acceptedMimes.push(...mimeTypes[type].type);
        }
      });
      return acceptedMimes;
    },
    getAllowedFilesExtensions(state) {
      let extensions = pathOr(
        null,
        ["data", "config", "allowed_extensions"],
        state
      );
      if (extensions && extensions.length > 0) {
        extensions = extensions.split(",");
        extensions = extensions.map(str => str.trim().toLowerCase());
      } else {
        // default extensions
        extensions = ["pdf", "jpg", "jpeg", "gif", "png", "tif", "tiff"];
      }
      return extensions;
    },
    getShowTableMinimize(state) {
      return state.data.config.show_container_toggle
        ? parseInt(state.data.config.show_container_toggle)
        : 0;
    },
    getProductType(state) {
      return state.data.product_type;
    },
    getHideQuotationButton(state) {
      return state.data.config.hide_quotation
        ? parseInt(state.data.config.hide_quotation)
        : 0;
    },
    getProductPartModeShow(state) {
      const default_display = "poptext";
      if (state.data.config.hasOwnProperty("product_part_display_type")) {
        if (
          ["poptext", "tabs"].includes(
            state.data.config.product_part_display_type
          )
        ) {
          return state.data.config.product_part_display_type;
        }
      }
      return default_display;
    },
    getWebproductHashData(state) {
      return hash({
        webproductId: this.getWebProductId,
        name: state.data.name,
        type: state.data.type,
        store_code: this.getStoreCode
      });
    },
    getSweetAlert(state) {
      return this.getVueInstance._context.config.globalProperties.$swal;
    },
    getVueInstance(state) {
      return state.vueInstance;
    },
    getIsMobile(state) {
      return state.isMobile;
    },
    getIntegrationIdConfig(state) {
      if (state.config && state.config.integrationId) {
        return state.config.integrationId;
      }
      return null;
    },
    getStoreCode(state) {
      if (state.storeCode) {
        return state.storeCode;
      }
      if (state.config && state.config.storeCode) {
        return state.config.storeCode;
      }
      return null;
    },
    getMatrixConfiguration(state) {
      if (state.data.themeMatrix) {
        var themeCss = Object.keys(state.data.themeMatrix).map(el => {
          return el + ":" + state.data.themeMatrix[el];
        });

        var fontStyle = "";
        if (state.data.themeMatrixFonts) {
          for (var font of state.data.themeMatrixFonts) {
            fontStyle += " " + font;
          }
        }

        if (themeCss.length) {
          if (!state.data.themeMatrix["--pq_mainColor"]) {
            themeCss.push("--pq_mainColor : #3141a7");
          }
          return (
            "<style>:root{" + themeCss.join(";") + "} " + fontStyle + "</style>"
          );
        }
      } else if (state.config && state.config.theme) {
        // we do want to load themes,
        // but the data is not present yet
        return "<style>:root{--pq_mainColor:transparent}</style>";
      }
      return null;
    },
    getInstanceConfig(state) {
      return state.config;
    },
    getIsSimpleConfigurationProduct(state) {
      return [
        "upload_without_configuration",
        "upload_configuration",
        "personalization_configuration",
        "upload_personalization_configuration",
        "upload_personalization_configuration_packaging"
      ].includes(state.data.product_type);
    },
    getWebProductSku(state) {
      return state.webProductSku;
    },
    getShowAddToCart(state) {
      return state.data.config.isLoggedIn
        ? parseInt(state.data.config.isLoggedIn)
        : 1;
    },
    getTheme(state) {
      return state.theme;
    },
    getPackingUnit(state) {
      return state.data.config.pck_unit &&
        parseInt(state.data.config.pck_unit) > 0
        ? parseInt(state.data.config.pck_unit)
        : 1;
    },
    getHideSearchBoxInPoptext(state) {
      return state.data.config.hide_searchbox_poptext
        ? parseInt(state.data.config.hide_searchbox_poptext)
        : 0;
    },
    getPersonalizationQuantityWithPrice(state) {
      if (
        state.data.product_type == "personalization" &&
        state.data.config.quantity_show_price_personalization &&
        this.getQuantitySelection &&
        this.getQuantitySelection != ""
      ) {
        return true;
      }
      return false;
    },
    getMeasurementSystem(state) {
      return state.data.config.measuring_system
        ? state.data.config.measuring_system
        : "";
    },
    getOtherOptionsDenyRules(state) {
      const changeOptionsStore = useChangeOptionsStore();
      var denyRules = Array.isArray(
        changeOptionsStore.getDenyRules["prod_opts"]
      )
        ? null
        : changeOptionsStore.getDenyRules["prod_opts"];

      return denyRules;
    },
    getPackingTdTextures(state) {
      return state.data.template_config && state.data.template_config.td_data
        ? state.data.template_config.td_data
        : null;
    },
    getSelectedTemplateId(state) {
      var activeTemplates = this.getActiveTemplates;
      return state.selectedTemplateId == null
        ? Object.keys(activeTemplates)[0]
        : state.selectedTemplateId;
    },
    getWebProductEnableTemplateChooser(state) {
      var activeTemplates = this.getActiveTemplates;
      if (
        Object.keys(activeTemplates).length > 1 &&
        state.data.template_chooser &&
        parseInt(state.data.template_chooser) == 0
      ) {
        return true;
      }
      return false;
    },
    getWebProductEnableTemplates(state) {
      var templates = [];
      var activeTemplates = this.getActiveTemplates;
      for (var id in activeTemplates) {
        templates.push({
          id,
          text: activeTemplates[id]
        });
      }

      return templates;
    },
    getWebProductTemplate(state) {
      var activeTemplates = this.getActiveTemplates;
      if (
        state.data.template_chooser &&
        parseInt(state.data.template_chooser) == 1
      ) {
        return Object.keys(activeTemplates);
      }
      if (
        typeof activeTemplates === "object" &&
        Object.keys(activeTemplates).length > 0
      ) {
        return this.getSelectedTemplateId;
      }
      return pathOr(0, ["data", "template"], state);
    },
    getActiveTemplates(state) {
      const changeOptionsStore = useChangeOptionsStore();
      var changeOptionsTemplate = changeOptionsStore.getChangeOptionsTemplate;
      if (changeOptionsTemplate) {
        return changeOptionsTemplate;
      }
      return pathOr({}, ["data", "template"], state);
    },
    getQuantitySelection(state) {
      return state.data.config.quantity_selection;
    },
    getSelector(state) {
      return state.selector;
    },
    getNumberOfActiveUploads(state) {
      return state.numberOfActiveUploads;
    },
    getFilesConnectLoading(state) {
      return state.filesConnectLoading;
    },
    getConnectFolder(state) {
      return state.selectionId;
    },
    getFilesNeedToBeConnected(state) {
      return state.filesNeedToBeConnected;
    },
    getIsPrintingProduct(state) {
      return state.data.config.printing_product;
    },
    getIsImageEditorProduct(state) {
      return state.data.config.enable_image_editor | 0;
    },
    getIsStockDefaultPDfConfig(state) {
      return state.data.product_type === "stock_configuration_default_pdf";
    },
    getNetworkError(state) {
      return state.networkError;
    },
    getNetworkMessage(state) {
      return state.networkMessage;
    },
    getHasAutomaticConnect(state) {
      if (!state.data.config.hasOwnProperty("enable_automatic_connect")) {
        return constants.AUTOMATIC_CONNECT_NOT_PRESENT;
      }
      return state.data.config.enable_automatic_connect &&
        parseInt(state.data.config.enable_automatic_connect)
        ? constants.AUTOMATIC_CONNECT_TRUE
        : constants.AUTOMATIC_CONNECT_FALSE;
    },
    getNumberOfPP(state) {
      return state.data.productparts.length;
    },
    getIsLfp(state) {
      return state.data.product_type === "image_editor";
    },
    getNumberOfUploads(state) {
      return state.numberOfUploads.length;
    },
    getLayoutType(state) {
      return state.data.type;
    },
    getConfig(state) {
      return state.data.config;
    },
    getProductName(state) {
      return state.data.name;
    },
    getWebProductData(state) {
      return state.data;
    },
    getActiveProductPartCode(state) {
      return state.activeProductPartCode;
    },
    getWebProductId(state) {
      return state.webProductId;
    },
    getAllActivePageOptions(state) {
      return state.activePageOptions;
    },
    getActivePageOptions(state) {
      return state.activePageOptions[state.activeProductPartCode];
    },
    getActivePageOptionsForPP(state) {
      return function (code) {
        const ppcode = code ? code : state.activeProductPartCode;
        return state.activePageOptions[ppcode];
      };
    },
    getWebProductName(state) {
      return state.data.name;
    },
    getActiveProductPart(state) {
      if (!state.data.productparts) {
        return [];
      }
      return state.data.productparts.filter(item => {
        return item.code == state.activeProductPartCode;
      });
    },
    getActiveProductPartName(state) {
      var activeProductPart = this.getActiveProductPart;
      return activeProductPart.length > 0 ? activeProductPart[0].label : "";
    },
    getProductPartByCode(state) {
      return function (code) {
        return state.data.productparts.filter(item => {
          return item.code == code;
        });
      };
    },
    getActiveProductPageOptions(state) {
      return this.getAllProductPageOptions(false);
    },
    getProductPageOptionsForCode(state) {
      const that = this;
      return function (payload) {
        return that.getAllProductPageOptions({
          code: payload.code,
          all: false
        });
      };
    },
    getAllProductPageOptions(state) {
      return function (all) {
        const changeOptionsStore = useChangeOptionsStore();
        let allOptions = false;
        let activeProductPart = this.getActiveProductPart;
        if (typeof all === "object") {
          activeProductPart = this.getProductPartByCode(all.code);
          allOptions = all.all ? all.all : false;
        } else {
          allOptions = all ? true : false;
        }

        var allPageOptions =
          activeProductPart.length > 0 ? activeProductPart[0].page_options : {};
        const activeProductPartCode =
          activeProductPart.length > 0
            ? activeProductPart[0].code
            : this.getActiveProductPartCode;
        var options = Object.keys(allPageOptions)
          .filter(item => {
            return allOptions || allPageOptions[item].hidden == 0;
          })
          .reduce((obj, key) => {
            obj[key] = allPageOptions[key];
            return obj;
          }, {});

        var selectedOpt =
          changeOptionsStore.getChangeOptionsDataPPOpt[
            "PP_" + activeProductPartCode
          ];
        var denyRules = Array.isArray(changeOptionsStore.getDenyRules)
          ? null
          : changeOptionsStore.getDenyRules[activeProductPartCode];

        if (
          typeof changeOptionsStore.getDenyRules["global_rule"] !== "undefined"
        )
          denyRules = _.cloneDeep(
            mergeDenyRules(
              denyRules,
              changeOptionsStore.getDenyRules["global_rule"]
            )
          );

        var items = {};
        for (var op in options) {
          var item = JSON.parse(JSON.stringify(options[op]));
          item.label = i18n.t(PREFIX.PO + options[op].label);
          item.tooltip = options[op].tooltip;

          if (denyRules && denyRules[op]) {
            item["values"] = JSON.parse(
              JSON.stringify(
                options[op]["values"].filter(el => {
                  return !denyRules[op].includes(el.value);
                })
              )
            );
          }

          for (var value in item.values) {
            item.values[value].label = i18n.t(
              PREFIX.POV + item.values[value].label
            );
          }

          item["selected"] = selectedOpt[op];
          items[op] = item;
        }
        return items;
      };
    },
    getIsLoading(state) {
      return state.isLoading;
    },
    getSelectionId(state) {
      return state.selectionId;
    },
    getProductPartsNamesForUpload(state) {
      if (this.getUploadPersonalizationOneProduct) {
        // show just the one that has type content
        var pp = this.getUploadPersonalizationOneProductPPType("content");
        return [{ code: pp.code, label: pp.label }];
      }
      return this.getProductPartsNames;
    },
    getProductPartsNames(state) {
      return state.data.productparts.map(item => {
        return { code: item.code, label: item.label };
      });
    },
    getProductPartsCodes(state) {
      return state.data.productparts.map(item => {
        return item.code;
      });
    },
    getActiveProductOptionsSelection(state) {
      const that = this;
      return function (ppcode) {
        const changeOptionsStore = useChangeOptionsStore();
        var activeProductPart = ppcode
          ? that.getProductPartByCode(ppcode)
          : that.getActiveProductPart;
        var ppParts = changeOptionsStore.getChangeOptionsDataPPOpt;
        var ppSelection = ppParts["PP_" + activeProductPart[0].code];

        var activePageOption = that.getActivePageOptionsForPP(ppcode);
        if (!ppSelection[activePageOption]) {
          return [];
        }
        return ppSelection[activePageOption];
      };
    },
    getInitialChangeOptionsPostData(state) {
      const that = this;
      return function (makeCall) {
        const customStore = useCustomStore();
        const updatesStore = useUpdatesStore();
        const quoteStore = useQuoteStore();
        var qties = customStore.getCustomQuantitiesLabels;

        let postData = {
          webproduct: that.getWebProductId,
          custom_quantities: qties.length == 0 ? "" : qties
        };
        if (that.getStoreCode) {
          postData["store_code"] = that.getStoreCode;
        }
        if (updatesStore.getAppSelectionId) {
          postData["selection"] = that.getSelectionId;
        }
        var data = that.getWebProductData;
        for (let option in data.options) {
          if (option == "quantity") {
            // set the qty field also
            var default_value = data.options[option].default_value;
            var qty = 1;
            for (var value of data.options[option].values) {
              if (value.value == default_value) {
                qty = value.attributes["qty"];
                break;
              }
            }
            postData["qty"] = qty;
          }
          if (data.options[option].type == "multiple") {
            if (makeCall) {
              postData["print_options[" + option + "][" + option + "]"] =
                data.options[option].default_value;
            } else {
              postData["print_options[" + option + "][" + option + "][]"] =
                Array.isArray(data.options[option].default_value)
                  ? data.options[option].default_value.length > 0
                    ? data.options[option].default_value[0]
                    : ""
                  : data.options[option].default_value;
            }
          } else {
            postData["print_options[" + option + "][" + option + "][]"] =
              Array.isArray(data.options[option].default_value)
                ? data.options[option].default_value[0]
                : data.options[option].default_value;
          }
        }
        postData["print_options[PP][]"] = that.getActiveProductPartCode;

        for (let ppKey in data.productparts) {
          var pp = data.productparts[ppKey];
          for (let attribute in pp.page_options) {
            postData["print_options[PP_" + pp.code + "][" + attribute + "][]"] =
              Array.isArray(pp.page_options[attribute].default_value)
                ? pp.page_options[attribute].default_value[0]
                : pp.page_options[attribute].default_value;
          }
        }

        if (that.getPersonalizationQuantityWithPrice) {
          var qties = that.getQuantitySelection.split(",");
          var updatedQty = [];
          for (let qty in qties) {
            updatedQty.push("custom_qty_" + qties[qty]);
          }
          for (let qty in qties) {
            postData["custom_quantities"] = updatedQty;
          }
        }
        return { ...postData, ...quoteStore.getInitialPreflightCheckData };
      };
    },
    getOtherOptions(state) {
      const changeOptionsStore = useChangeOptionsStore();
      var options = state.data.options;
      var data = [];
      var deniedValues = this.getOtherOptionsDenyRules;
      for (var option in options) {
        if (options[option].hidden || option == "quantity") {
          continue;
        }
        var allValues = options[option].values;
        if (deniedValues && deniedValues[option]) {
          allValues = options[option].values.filter(el => {
            return !deniedValues[option].includes(el.value);
          });
        }

        var item = {
          type: options[option].type,
          code: option,
          tooltip: options[option].tooltip,
          name: i18n.t(PREFIX.PO + options[option].label),
          values: allValues.map(el => {
            return {
              text: i18n.t(PREFIX.POV + el.label),
              id: el.value,
              tooltip: el.tooltip
            };
          }),
          selected: changeOptionsStore.getChangeOptionsDataPPOpt[option][option] // ????
        };
        data.push(item);
      }
      return data;
    },
    getProductPartsOption(state) {
      const bundleStore = useBundleStore();
      let values = state.data.productparts.map(el => {
        return {
          text: i18n.t(PREFIX.PP + el.label),
          id: el.code
        };
      });
      let activePp = this.getActiveProductPartCode;
      if (bundleStore.getBundleIsActive) {
        values = bundleStore.getBundleAllActiveProductParts;
        activePp = bundleStore.getBundleTheActiveProductPart;
      }
      return {
        multiselect: 0,
        code: productPartsOptionCode,
        name: i18n.t("Product Parts"),
        values,
        selected: [activePp]
      };
    },
    getOptions(state) {
      const bundleStore = useBundleStore();
      const packingOptionsStore = usePackingOptionsStore();
      const customStore = useCustomStore();
      const ppCode = bundleStore.getBundleIsActive
        ? bundleStore.getBundleTheActiveProductPart
        : this.getActiveProductPartCode;
      var activePPOptions = this.getProductPageOptionsForCode({
        code: ppCode,
        all: false
      });
      var data = [];
      var item = {};

      // add the product parts if we have more than one
      if (state.data.productparts.length > 1) {
        item = this.getProductPartsOption;
        data.push(item);
      }

      for (var option in activePPOptions) {
        if (activePPOptions[option].hidden) {
          continue;
        }

        item = {
          multiselect: 0,
          code: option,
          //name: i18n.t(PREFIX.PO + activePPOptions[option].label),
          name: activePPOptions[option].label,
          is_popup: activePPOptions[option].is_popup,
          is_frame: activePPOptions[option].is_frame,
          popup_title: activePPOptions[option].popup_title,
          values: activePPOptions[option].values.map(el => {
            return {
              //text: i18n.t(PREFIX.POV + el.label),
              text: el.label,
              id: el.value,
              tooltip: el.tooltip
            };
          }),
          tooltip: activePPOptions[option].tooltip,
          selected: activePPOptions[option].selected
        };

        if (option == "format" && this.getConfig.enable_custom_format) {
          //add custom formats
          if (this.getConfig.enable_packaging) {
            for (var pack in packingOptionsStore.getPackingCustomFormats) {
              item.values.push({
                text: packingOptionsStore.getPackingCustomFormats[pack].label,
                id: packingOptionsStore.getPackingCustomFormats[pack].value
              });
            }
          } else {
            for (var cust of customStore.getCustomFormats) {
              item.values.push({
                text: cust.label,
                id: cust.value
              });
            }
          }
        }

        data.push(item);
      }
      return data;
    },
    getProductPartsOptionCode(state) {
      return productPartsOptionCode;
    },
    getShowPreviewInUpload(state) {
      return state.data.config.hasOwnProperty("upload_previews")
        ? parseInt(state.data.config.upload_previews)
        : 1;
    },
    getShouldRenderComponent(state) {
      return function (name) {
        if (
          parseInt(state.data.config.disable_upload) &&
          ["Upload", "UploadButton", "UploadContainer", "UploadTable"].includes(
            name
          )
        ) {
          return false;
        }
        if (
          !parseInt(state.data.config.enable_custom_quantity) &&
          ["InputQuantity"].includes(name)
        ) {
          return false;
        }
        if (
          !parseInt(state.data.config.personalize) &&
          ["PersonalizationButton"].includes(name)
        ) {
          return false;
        }
        if (
          !parseInt(state.data.config.enable_custom_format) &&
          ["InputCustomFormatInsert", "InputCustomFormat"].includes(name)
        ) {
          return false;
        }
        if (
          !parseInt(state.data.config.show_matrix) &&
          [
            "ProductPartsNamesContainer",
            "ProductPartsNames",
            "ProductParts"
          ].includes(name)
        ) {
          return false;
        }

        if (["PackingOptions", "PackingOptionsContainer"].includes(name)) {
          if (
            parseInt(state.data.config.enable_custom_format) &&
            parseInt(state.data.config.enable_packaging)
          ) {
            return true;
          }
          return false;
        }

        return true;
      };
    },
    getPackagingVariables(state) {
      return pathOr(
        [],
        ["data", "packing_config", "packaging_variables"],
        state
      );
    },
    getPackagingModel(state) {
      return pathOr("", ["data", "packing_config", "model"], state);
    },
    getInputUnit(state) {
      return pathOr("mm", ["data", "packing_config", "Input_Unit"], state);
    },
    getIsPackingEnabled(state) {
      if (this.getConfig.enable_packaging) {
        return parseInt(this.getConfig.enable_packaging) ? true : false;
      }
      return false;
    },
    getEnableLoader(state) {
      return this.getConfig.enable_loader;
    },
    getIsPreflighPitstop(state) {
      return (
        this.getConfig.preflight_enabled &&
        this.getConfig.preflight_type == "pitstop"
      );
    },
    getIsPersonalizationNewPage(state) {
      return pathOr(1, ["data", "config", "isPersonalizationNewPage"], state);
    },
    getShowConnectButton(state) {
      if (this.getHasAutomaticConnect == constants.AUTOMATIC_CONNECT_FALSE) {
        if (
          this.getFilesNeedToBeConnected &&
          !this.getConfig.disable_upload &&
          !this.getConfig.personalize &&
          this.getNumberOfPP == this.getNumberOfUploads
        ) {
          return true;
        }
      }
      return false;
    },
    getPriceDisplayType(state) {
      const displayType = parseInt(
        pathOr(
          constants.TAX_DISPLAY_TYPE_EXCL_TAX,
          ["data", "config", "price_display_type"],
          state
        )
      );
      if (
        ![
          constants.TAX_DISPLAY_TYPE_EXCL_TAX,
          constants.TAX_DISPLAY_TYPE_INCL_TAX,
          constants.TAX_DISPLAY_TYPE_BOTH
        ].includes(displayType)
      ) {
        return constants.TAX_DISPLAY_TYPE_EXCL_TAX;
      }
      return displayType;
    },
    getIsValidForm(state) {
      const that = this;
      return function (widget) {
        const bundleStore = useBundleStore();
        const changeOptionsStore = useChangeOptionsStore();
        var valid = true;
        var isPrintingProduct = that.getIsPrintingProduct;
        var isImageEditorProduct = that.getIsImageEditorProduct;
        var hasAutomaticConnect = that.getHasAutomaticConnect;
        var isStockDefaultPDfConfig = that.getIsStockDefaultPDfConfig;
        var noPP = that.getNumberOfPP;
        var noUploads = that.getNumberOfUploads;
        var isLfp = that.getIsImageEditorProduct;

        if (isPrintingProduct || noPP > 0) {
          if (isPrintingProduct && !isLfp) {
            if (
              bundleStore.getBundleIsActive &&
              bundleStore.getBundleAllActiveProductParts.length == 1
            ) {
              that.getSweetAlert({
                text: i18n.t(
                  "Please configure at least another product part !"
                ),
                icon: "warning"
              });
              return false;
            }
            if (
              hasAutomaticConnect == constants.AUTOMATIC_CONNECT_NOT_PRESENT ||
              hasAutomaticConnect == constants.AUTOMATIC_CONNECT_TRUE
            ) {
              if (
                hasAutomaticConnect == constants.AUTOMATIC_CONNECT_TRUE &&
                changeOptionsStore.getMatrixOneSelection == false
              ) {
                // check that the user made at least one selection in matrix
                if (widget) {
                  that.getSweetAlert({
                    text: i18n.t("Please configure your product !"),
                    icon: "warning"
                  });
                }
                return false;
              }
              if (
                noPP !== noUploads &&
                !isImageEditorProduct &&
                !isStockDefaultPDfConfig
              ) {
                valid = false;
                if (widget) {
                  that.getSweetAlert({
                    text: i18n.t(
                      "Please make an upload for each product part!"
                    ),
                    icon: "warning"
                  });
                }
                return false;
              }
            }
          }
        }

        return valid;
      };
    },

    getCallChangeOptionPerType(state) {
      if (
        [
          "image_editor",
          "upload_configuration",
          "personalization_configuration",
          "stock_configuration_default_pdf",
          "upload_without_configuration",
          "upload_personalization_configuration",
          "upload_fullpage_configuration",
          "personalization_fullpage_configuration",
          "upload_personalization_configuration_packaging",
          "upload_personalization_fullpage_configuration"
        ].includes(state.data.product_type)
      ) {
        return true;
      }
      if (this.getPersonalizationQuantityWithPrice) {
        return true;
      }
      return false;
    },
    getWhiteMarginIsUsed(state) {
      var activePPOptions = this.getAllProductPageOptions(true);
      for (var option in activePPOptions) {
        var values = activePPOptions[option]["values"].filter(el => {
          return activePPOptions[option].selected[0] == el.value;
        });
        if (values.length) {
          values = values[0];
          if (
            typeof values["has_white_border"] != "undefined" &&
            parseInt(values["has_white_border"])
          )
            return true;
        }
      }
      return false;
    },
    getHasFrameOptions(state) {
      var activePPOptions = this.getActiveProductPageOptions;
      for (var option in activePPOptions) {
        if (activePPOptions[option].hidden) {
          continue;
        }
        if (activePPOptions[option].is_frame) {
          var values = activePPOptions[option]["values"].filter(el => {
            return activePPOptions[option].selected[0] == el.value;
          });
          if (values.length) {
            values = values[0];
            if (
              typeof values.attributes["border_width"] != "undefined" &&
              parseFloat(values.attributes["border_width"]) &&
              values.texture_border
            )
              return {
                hasFrame: true,
                imageData: {
                  texture_border: values.texture_border,
                  border_width: parseFloat(values.attributes["border_width"])
                }
              };
          }
        }
      }
      return {
        hasFrame: 0
      };
    }
  },

  actions: {
    setHideInitialLoading(payload) {
      this.hideInitialLoading = payload;
    },
    setVueInstance(payload) {
      this.vueInstance = payload;
    },
    setIsMobile(payload) {
      this.isMobile = payload;
    },
    setStoreCode(payload) {
      this.storeCode = payload;
    },
    setTheme(payload) {
      this.theme = payload;
    },
    setSelector(payload) {
      this.selector = payload;
    },
    setFilesConnectLoading(payload) {
      this.filesConnectLoading = payload;
    },
    setFilesNeedToBeConnected(payload) {
      this.filesNeedToBeConnected = payload;
    },
    setNetworkError(payload) {
      if (payload == null) {
        this.networkError = false;
        this.networkMessage = "";
      } else {
        this.networkError = true;
        this.networkMessage =
          i18n.t("Error connecting to server") +
          " : " +
          (payload.message ? i18n.t(payload.message) : i18n.t("Network Error"));
      }
    },
    setWebProductData(payload) {
      this.data = { ...this.data, ...payload };
      this.activeProductPartCode = payload.productparts[0].code;
      var activePageOptions = {};
      for (var key in payload.productparts) {
        var obj = payload.productparts[key];
        var visibleProductParts = Object.keys(obj.page_options).filter(item => {
          return obj.page_options[item].hidden == 0;
        });

        activePageOptions[obj.code] = visibleProductParts[0];
      }
      this.activePageOptions = activePageOptions;
    },
    setActiveProductPageOption(payload) {
      var activePageOptions = { ...this.activePageOptions };
      const ppcode = payload.ppcode
        ? payload.ppcode
        : this.activeProductPartCode;
      activePageOptions[ppcode] = payload.option;
      this.activePageOptions = activePageOptions;
    },
    setActiveProductPartName(payload) {
      const bundleStore = useBundleStore();
      const configStore = useConfigStore();
      this.activeProductPartCode = payload;
      if (bundleStore.getBundleIsActive) {
        bundleStore.setBundleOpenPP(payload);
      }
      //configStore.setShowUpload(false);
    },
    setIsLoading(payload) {
      this.isLoading = payload;
    },
    setWebProductId(payload) {
      this.webProductId = payload;
    },
    setWebProductSku(payload) {
      this.webProductSku = payload;
    },
    setSelectionId(payload) {
      this.selectionId = payload;
    },
    uploadFile(payload) {
      const configStore = useConfigStore();
      const bundleStore = useBundleStore();
      if (!this.numberOfUploads.includes(payload)) {
        this.numberOfUploads = [...this.numberOfUploads, payload];
      }
      configStore.setPersonalizationFullPage(false);
      if (
        this.getUploadPersonalizationOneProduct &&
        this.getNumberOfUploads == 1
      ) {
        configStore.setAddToCartFullPage(true);
      } else if (this.getNumberOfUploads == this.getNumberOfPP) {
        configStore.setAddToCartFullPage(true);
      }
    },
    removeUploadedFile(payload) {
      const configStore = useConfigStore();
      const bundleStore = useBundleStore();
      if (this.numberOfUploads.includes(payload)) {
        this.numberOfUploads = this.numberOfUploads.filter(el => {
          return el != payload;
        });
      }
      if (this.getNumberOfUploads == 0) {
        configStore.setPersonalizationFullPage(true);
      }
      configStore.setAddToCartFullPage(false);
    },
    uploadLoading(payload) {
      if (payload) {
        this.numberOfActiveUploads++;
      } else {
        this.numberOfActiveUploads--;
      }
    },
    setSelectedTemplateId(payload) {
      this.selectedTemplateId = payload;
    },
    setInstanceConfig(payload) {
      this.config = payload;
    },

    connectUploads(payload) {
      const personalizeStore = usePersonalizeStore();
      const urlsStore = useUrlsStore();
      const configStore = useConfigStore();
      personalizeStore.setPersonalizeLoading(true);

      axios.instance
        .post(
          urlsStore.getUrl(axios.connectUpload),
          qs.stringify({
            selection: this.getConnectFolder,
            store_code: this.getStoreCode
          })
        )
        .then(response => {
          if (response.data.success) {
            // files connected
            this.setFilesNeedToBeConnected(false);
            configStore.setDisableDeleteUploadAfterConnect(true);
          } else if (response.data.message) {
            // files connected
            this.getSweetAlert({
              text: "Error " + response.data.message,
              icon: "error"
            });
          }
          personalizeStore.setPersonalizeLoading(false);
        })
        .catch(error => {
          personalizeStore.setPersonalizeLoading(false);
          console.log("Error " + error);
          this.getSweetAlert({
            text: "Error " + error,
            icon: "error"
          });
        });
    },
    loadInitialData(payload) {
      const urlsStore = useUrlsStore();
      const customerStore = useCustomerStore();
      const changeOptionsStore = useChangeOptionsStore();
      const personalizeStore = usePersonalizeStore();
      const myAccountStore = useMyAccountStore();
      const updatesStore = useUpdatesStore();

      if (payload.config) {
        this.setInstanceConfig(payload.config);
      }
      this.setWebProductId(payload.webProductId);
      this.setWebProductSku(payload.webProductSku);
      this.setSelectionId(payload.selectionId);

      if (payload.theme) {
        this.setTheme(payload.theme);
      }
      if (payload.urls) {
        urlsStore.setUrls(payload.urls);
      }
      if (payload.customer) {
        customerStore.setCustomer(payload.customer);
      }

      this.setSelector(payload.selector);
      this.setNetworkError(null);
      if (payload.isMyAccount) {
        changeOptionsStore.setCallChangeOptions(false);
        myAccountStore.setMyAccount({
          isMyAccount: payload.isMyAccount,
          myAccountSelection: payload.myAccountSelection,
          myAccountUploadedFiles: payload.myAccountUploadedFiles,
          myAccountUploadPath: payload.myAccountUploadPath
        });
      }

      if (payload.initialConfig) {
        if (payload.initialConfig.version) {
          updatesStore.setDatacenterVersion(payload.initialConfig.version);
          if (payload.initialConfig.language) {
            setLanguageAttributes({
              lang: payload.initialConfig.language.lang,
              translations: payload.initialConfig.language.translations
            });
          }
          this.setWebProductId(payload.initialConfig.webproduct_id);
          this.setStoreCode(payload.initialConfig.store_code);
          if (!this.getSelectionId) {
            this.setSelectionId(getSelectionUuid(this));
          }
          updatesStore.setAppSelectionId(true);

          jQuery("body").addClass(
            "printq_" + payload.initialConfig.product_type
          );
        }
        this.setWebProductData(payload.initialConfig);
        afterInitialConfigData(this, payload.initialConfig);
        return;
      }

      var url = urlsStore.getUrl(axios.apiGetConfig) + this.getWebProductId;
      if (payload.getConfigTokenData) {
        // call get with Tokenized data
        url =
          urlsStore.getUrl(axios.apiGet) + "?jwt=" + payload.getConfigTokenData;
      }

      if (this.getIntegrationIdConfig) {
        var getConfigUrl = urlsStore.getUrl(axios.apiGetConfig);
        if (getConfigUrl.charAt(getConfigUrl.length - 1) == "/") {
          getConfigUrl = getConfigUrl.slice(0, -1);
        }
        let param = "jwt";
        if (!this.getIntegrationIdConfig.includes(".")) {
          param = "integration";
        }
        url = getConfigUrl + `?${param}=${this.getIntegrationIdConfig}`;
      }

      // for shopify workflow
      const quote = getQuoteParam();
      if (quote) {
        url += "&quote=" + quote;
      }

      axios.instance
        .get(url)
        .then(response => {
          if (payload.config && payload.config.configLanguage) {
            if (response.data.data.language) {
              setLanguageAttributes({
                lang: response.data.data.language.lang,
                translations: response.data.data.language.translations
              });
            }
          }
          if (
            response.data.hasOwnProperty("success") &&
            response.data.success === 0
          ) {
            console.log(
              "Error : could not get configuration data " +
                response.data.message
            );
            this.setIsLoading(false);
            this.setNetworkError({
              message:
                "Could not get configuration data - " + response.data.message
            });
            changeOptionsStore.callInitialLoadingFinalized({
              type: "getData",
              status: 0
            });
            return;
          }
          var responseData = response.data;
          if (response.data.hasOwnProperty("success")) {
            // new way with Integration
            responseData = response.data.data;
            this.setWebProductId(responseData.webproduct_id);
            this.setStoreCode(responseData.store_code);
            updatesStore.setDatacenterVersion(responseData.version);
            if (!this.getSelectionId) {
              this.setSelectionId(getSelectionUuid(this));
            }
            if (quote) {
              this.setSelectionId(quote);
            }

            updatesStore.setAppSelectionId(true);

            jQuery("body").addClass("printq_" + responseData.product_type);
          }

          this.setWebProductData(responseData);
          afterInitialConfigData(this, responseData);
        })
        .catch(error => {
          console.log("Error " + error);
          this.setIsLoading(false);
          this.setNetworkError(error);
          changeOptionsStore.callInitialLoadingFinalized({
            type: "getData",
            status: 0
          });
        });
    },
    setChangeOptionsTemplate(payload) {
      var templateIds = Object.keys(this.getActiveTemplates);
      if (templateIds.length) {
        if (!templateIds.includes(this.getSelectedTemplateId)) {
          this.setSelectedTemplateId(templateIds[0]);
        }
      }
    },
    openFancybox(payload) {
      const defaulOption = {
        type: "iframe",
        width: "100%",
        height: "100%",
        iframe: {
          preload: true, // false
          css: {
            width: "100%",
            height: "100%",
            "max-width": "100%",
            "max-height": "100%"
          }
        },
        opts: {
          iframe: {
            preload: true,
            css: {
              width: "100%",
              height: "100%",
              "max-width": "100%",
              "max-height": "100%"
            }
          },
          afterShow: function (instance, current) {
            toggleBodyScroll(false);
          },
          afterClose: function (instance, current) {
            toggleBodyScroll(true);
          }
        }
      };
      window.fancyboxInstance = jQuery.fancybox.open({
        ...defaulOption,
        ...payload
      });
    },
    closeFancybox() {
      if (window.fancyboxInstance) {
        window.fancyboxInstance.close();
      }
    }
  }
});

const toggleBodyScroll = function (status) {
  if (document.body.parentElement) {
    if (!status) document.body.parentElement.style.overflow = "hidden";
    else document.body.parentElement.style.overflow = "";
  }
};

const afterInitialConfigData = (webproductStore, initialData) => {
  const checkedPageOptionsStore = useCheckedPageOptionsStore();
  const customerNotesStore = useCustomerNotesStore();
  const imageEditorStore = useImageEditorStore();
  const bundleStore = useBundleStore();
  const customStore = useCustomStore();
  const changeOptionsStore = useChangeOptionsStore();
  const uploadsStore = useUploadsStore();
  const quoteStore = useQuoteStore();
  const personalizeStore = usePersonalizeStore();
  const configStore = useConfigStore();
  checkedPageOptionsStore.setCheckedPOData(
    webproductStore.getWebProductData.productparts
  );
  window.printqMatrixConfig = webproductStore.getWebProductData;
  window.webproductId = webproductStore.getWebProductId;
  if (initialData && initialData.image_editor) {
    imageEditorStore.loadImageEditorData(initialData.image_editor);
  }
  if (webproductStore.getWebProductData.quoteMode) {
    quoteStore.setIsQuoteMode(true);
    quoteStore.setQuoteSelection(
      webproductStore.getWebProductData.quoteSelection
    );
    if (webproductStore.getWebProductData.edit_files) {
      for (const edit_file in webproductStore.getWebProductData.edit_files) {
        const file = {
          file_info: {
            ...webproductStore.getWebProductData.edit_files[edit_file]
          },
          file_url: webproductStore.getWebProductData.edit_files[edit_file].url,
          uploadedFile: edit_file
        };
        quoteStore.setDefaultSelectionUploads({
          pp: webproductStore.getWebProductData.edit_files[edit_file].code,
          file,
          setWebproductDefaultSelection: 1
        });
      }
    }

    if (webproductStore.getWebProductData.quoteCustomizedProductId) {
      personalizeStore.setCustomizedProductId({
        customizedProductId:
          webproductStore.getWebProductData.quoteCustomizedProductId
      });
    }

    quoteStore.setEditUploadedFiles(
      webproductStore.getWebProductData.edit_files
    );
    configStore.setAddToCartFullPage(true);
  }
  bundleStore.setBundlePPData();
  customStore.loadCustomFromLocalStorage();
  changeOptionsStore.callInitialLoadingFinalized({
    type: "get",
    status: 1
  });
  changeOptionsStore.changeOptions({ initialLoading: 1 });
  uploadsStore.setInitialUploads(webproductStore.getProductPartsCodes);

  customerNotesStore.setInitialCustomerNotes(
    Object.keys(webproductStore.getCustomerNotes)
  );

  addDefaultTranslations(
    webproductStore.getWebProductData.options,
    webproductStore.getWebProductData.productparts
  );
};

const getSelectionUuid = webproductStore => {
  if (
    webproductStore.getInstanceConfig &&
    webproductStore.getInstanceConfig.selectionSize
  ) {
    if (webproductStore.getInstanceConfig.selectionSize == 32) {
      const uuid = uuidv4();
      return uuid.replaceAll("-", "");
    }
  }
  return uuidv4();
};

const getQuoteParam = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get("quote");
};
